// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/HypothesesCard.tsx (ADAPT)
// ADAPTED (plan P4-4, P4-2, P4-6, H3): rendered under the assessment (TP shows it in the chat). The hypotheses are the BFF's
// (the contract's `Hypothesis`, previews with our images). A hypothesis with no preview (`preview: null`) uses a
// text-only layout. Every image goes through `safeHttpUrl`; one that does not pass is left out. "Run this" and "See more" are
// disabled with a hint that planning starts after signup (H3, TD-4.5). `@container` / `@[560px]` become `sm:`.
/**
 * The team's first hypotheses, in the conversation once the cohort is built:
 * one card each, the preview of the ad or the funnel on the left, then what it
 * moves and by how much, and a button to run it. The cards stack; in a narrow chat the preview goes on top.
 */
import { ArrowRight, Check, ChevronRight, Play, Signal, Sparkles } from 'lucide-react'
import { Badge } from '@/components/ui/shadcn/badge'
import { Button } from '@/components/ui/shadcn/button'
import { Card } from '@/components/ui/shadcn/card'
import type { Hypothesis, HypothesisPreview } from '@/contracts/onboarding-session'
import { safeHttpUrl } from '@/lib/safeHttpUrl'
import { cn } from '@/lib/utils'
import { ASSESSMENT_COPY } from '../assessment-copy'
import { CHECK_LABELS } from '../growth-score-labels'

/** The preview to draw, or null for the text-only layout: no preview, or none of its pictures is a usable http(s) address. */
export function drawablePreview(preview: HypothesisPreview | null): HypothesisPreview | null {
  if (!preview) return null
  if (preview.kind === 'frames') {
    const frames = preview.frames.flatMap((f) => {
      const src = safeHttpUrl(f.src)
      return src ? [{ ...f, src }] : []
    })
    return frames.length ? { ...preview, frames } : null
  }
  return preview
}

export function HypothesesCard({ brand, hypotheses }: { brand: string; hypotheses: Hypothesis[] }) {
  if (hypotheses.length === 0) return null
  return (
    <section aria-label={`Top recommendations for ${brand}`} className="flex w-full flex-col gap-3">
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="text-heading-sm text-foreground">Top recommendations for {brand}</h3>
        <p className="text-label text-ds-text-2">
          {hypotheses.length} to start with
        </p>
      </header>
      <ol className="flex flex-col gap-3">
        {hypotheses.map((h, i) => {
          const preview = drawablePreview(h.preview)
          const hintId = `hypothesis-hint-${h.id}`
          return (
          <li key={h.id} className="min-w-0 animate-in fade-in slide-in-from-bottom-1 duration-300 motion-reduce:animate-none" style={{ animationDelay: `${i * 120}ms` }}>
            <Card className="flex flex-col overflow-hidden sm:flex-row">
              {preview ? (
                <div className="flex shrink-0 items-center justify-center border-b border-ds-hairline bg-ds-paper-2 p-4 sm:w-[280px] sm:border-b-0 sm:border-r">
                  <Preview preview={preview} />
                </div>
              ) : null}
              <div className="flex min-w-0 flex-1 flex-col gap-3 p-4">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge intent={h.area === 'Ad Creative' ? 'info' : 'success'} dot>
                    {h.area}
                  </Badge>
                  <Badge intent="neutral">
                    <Signal aria-hidden className="size-3.5" />
                    {h.confidence}
                  </Badge>
                </div>
                <div className="flex flex-col gap-1">
                  <h4 className="text-heading-sm text-foreground">{h.title}</h4>
                  <p className="text-body-sm text-ds-text-2">{h.text}</p>
                </div>
                <dl className="mt-auto flex items-center gap-5 border-t border-ds-hairline pt-3">
                  <div className="flex flex-col">
                    <dt className="text-label text-ds-text-3">Target metric</dt>
                    <dd className="text-tab text-foreground">{h.targetMetric}</dd>
                  </div>
                  <div className="flex flex-col border-l border-ds-hairline pl-5">
                    <dt className="text-label text-ds-text-3">Score impact</dt>
                    <dd className="text-tab text-foreground">
                      +{h.impact.points} pts <span className="text-label text-ds-text-2">on {CHECK_LABELS[h.impact.on].name}</span>
                    </dd>
                  </div>
                </dl>
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" disabled aria-describedby={hintId}>
                    <Play aria-hidden />
                    Run this
                  </Button>
                  <Button type="button" variant="ghost" disabled aria-describedby={hintId}>
                    See more
                    <ChevronRight aria-hidden />
                  </Button>
                  <span id={hintId} className="text-label text-ds-text-3">
                    {ASSESSMENT_COPY.planningAfterSignup}
                  </span>
                </div>
              </div>
            </Card>
          </li>
          )
        })}
      </ol>
    </section>
  )
}

/** A sketch of the ad or the funnel step the hypothesis changes. */
function Preview({ preview }: { preview: HypothesisPreview }) {
  if (preview.kind === 'frames') {
    return (
      <ul className="flex w-full gap-1.5" aria-label="Ad frames">
        {preview.frames.map((f, i) => (
          <li key={`${i}-${f.caption}`} className="relative min-w-0 flex-1 overflow-hidden rounded-sm">
            <Picture src={f.src} className="aspect-[3/4] w-full object-cover object-top" />
            <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ds-ink to-transparent px-1.5 pb-1.5 pt-4 text-label text-ds-paper">{f.caption}</span>
          </li>
        ))}
      </ul>
    )
  }
  if (preview.kind === 'product') {
    return (
      <div className="flex w-full gap-2.5 rounded-md border border-ds-hairline bg-card p-2.5" aria-label="Product page">
        <Picture src={preview.image} className="w-14 shrink-0 rounded-sm object-cover object-top" />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span aria-hidden className="h-1.5 w-3/4 rounded-full bg-ds-ink-a10" />
          <span aria-hidden className="mb-0.5 h-1.5 w-1/2 rounded-full bg-ds-ink-a10" />
          {preview.options.map((o, i) => (
            <span
              key={`${i}-${o.label}`}
              className={cn(
                'flex items-center gap-1 rounded-xs border px-1.5 py-1 text-label',
                o.selected ? 'border-ds-ink text-foreground' : 'border-ds-hairline text-ds-text-2',
              )}
            >
              {o.selected ? <Check aria-hidden className="size-3.5 shrink-0 text-foreground" /> : null}
              <span className="min-w-0 truncate">{o.label}</span>
              {o.note ? <span className="shrink-0 rounded-xs bg-ds-ink px-1 text-caption text-ds-paper">{o.note}</span> : null}
              <span className="ml-auto shrink-0 text-meta">{o.price}</span>
            </span>
          ))}
          <span className="flex h-6 items-center justify-center rounded-xs bg-ds-ink text-label text-ds-paper">{preview.cta}</span>
        </div>
      </div>
    )
  }
  return (
    <ol className="flex w-full items-center gap-1" aria-label="Quiz funnel">
      <li className="flex min-w-0 flex-col items-center gap-1">
        <Picture src={preview.ad} className="h-14 w-10 rounded-sm object-cover object-top" />
        <span className="text-label text-ds-text-3">Ad</span>
      </li>
      <ArrowRight aria-hidden className="size-3.5 shrink-0 text-ds-text-3" />
      <li className="flex min-w-0 flex-1 flex-col gap-1 rounded-md border border-ds-ink bg-card p-2">
        <span className="flex items-center gap-1 truncate text-label text-foreground">
          <Sparkles aria-hidden className="size-3.5 shrink-0" />
          {preview.title}
        </span>
        <span className="text-label text-foreground">{preview.question}</span>
        {preview.options.map((o, i) => (
          <span key={`${i}-${o}`} className={cn('truncate rounded-xs border px-1.5 py-0.5 text-label', o === preview.picked ? 'border-ds-ink text-foreground' : 'border-ds-hairline text-ds-text-3')}>
            {o}
          </span>
        ))}
        <span aria-hidden className="mt-0.5 flex gap-0.5">
          <span className="h-1 flex-1 rounded-full bg-ds-ink" />
          <span className="h-1 flex-1 rounded-full bg-ds-ink-a10" />
          <span className="h-1 flex-1 rounded-full bg-ds-ink-a10" />
        </span>
      </li>
      <ArrowRight aria-hidden className="size-3.5 shrink-0 text-ds-text-3" />
      <li className="flex min-w-0 flex-col items-center gap-1">
        <Picture src={preview.match} className="h-14 w-10 rounded-sm object-cover object-top" />
        <span className="text-label text-ds-text-3">Match</span>
      </li>
    </ol>
  )
}

/** One picture of ours, http(s) only and without a referrer; a tile of the same size when it is not drawable. */
function Picture({ src, className }: { src: string; className: string }) {
  const safe = safeHttpUrl(src)
  return safe ? <img src={safe} alt="" loading="lazy" referrerPolicy="no-referrer" className={className} /> : <span aria-hidden className={cn('block bg-ds-ink-a10', className)} />
}
